import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, FormStore, inject, Input, Loader, Modal, ModalHandle, TasksStore } from 'Mri.Web.Common';
import * as React from 'react';
import { BsPencilSquare } from '@react-icons/all-files/bs/BsPencilSquare';
import { BsWifi } from '@react-icons/all-files/bs/BsWifi';
import { LinkContainer } from 'react-router-bootstrap';
import { Collapse, Form, FormGroup } from 'reactstrap';
import { AppStore } from '../Core/AppStore';
import { NocServersStore } from './NocServersStore';

export interface INocServerRowProps {
    readonly nocServer: Mri.NocServers.NocServer;
}

@autoinject
@observer
export class NocServerItem extends React.Component<INocServerRowProps> {
    public render() {
        const location = `/noc-servers`;

        return (
            <div className="card d-block mb-3 p-2">
                <div className="card-body p-0">
                    <div className="w-100 d-flex">
                        <p className="mb-0 d-inline-block fw-bold">{this.props.nocServer.serverName} </p>

                        <div className="ms-auto d-inline-block">
                            <a
                                href={`http://${this.props.nocServer.remoteAppUri}:8005`}
                                className="btn btn-link fw-bold py-0 px-2 ml"
                            >
                                <BsWifi />
                            </a>

                            <LinkContainer
                                exact
                                to={location + `/${this.props.nocServer.serverName}`}
                                className="btn btn-link fw-bold p-0"
                            >
                                <a>
                                    <BsPencilSquare />
                                </a>
                            </LinkContainer>
                        </div>
                    </div>

                    <div>
                        <small className="d-block">
                            <strong>URI: </strong>
                            {this.props.nocServer.uri}
                        </small>
                    </div>

                    <div>
                        <small>
                            <strong>RemoteAppURI: </strong> {this.props.nocServer.remoteAppUri}
                        </small>
                    </div>

                    <div>
                        <small>
                            <strong>Proxy IP Address: </strong>
                            {this.props.nocServer.proxyIpAddress}
                        </small>
                    </div>

                    <div>
                        <small>
                            <strong>Proxy Port: </strong>
                            {this.props.nocServer.proxyPort}
                        </small>
                    </div>

                    <div className="d-flex w-100 justify-content-between">
                        <a onClick={this.onToggle} className="btn btn-link p-0">
                            <p className="mb-0">Details</p>
                        </a>
                    </div>

                    <Collapse isOpen={!this.collapsed}>
                        <div className="col-12 col-md-6 px-0 pt-1">
                            <Form>
                                <FormGroup color={this.editServerForm.fields.uri.state}>
                                    <label>URI</label>

                                    <Input
                                        type="text"
                                        field={this.editServerForm.fields.uri}
                                        placeholder="Name"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.editServerForm.fields.remoteAppUri.state}>
                                    <label>Remote App URI</label>

                                    <Input
                                        type="text"
                                        field={this.editServerForm.fields.remoteAppUri}
                                        placeholder="Remote App URI"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.editServerForm.fields.proxyIpAddress.state}>
                                    <label>Proxy IP Address</label>

                                    <Input
                                        type="text"
                                        field={this.editServerForm.fields.proxyIpAddress}
                                        placeholder="Proxy IP Address"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.editServerForm.fields.proxyPort.state}>
                                    <label>Proxy Port</label>

                                    <Input
                                        type="text"
                                        field={this.editServerForm.fields.proxyPort}
                                        placeholder="Proxy Port"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <a className="btn btn-primary" onClick={this.submitAsync}>
                                        <Loader loading={this.loading} showSpinner message="Updating..." />
                                        {!this.loading && 'Update'}
                                    </a>
                                </FormGroup>
                            </Form>
                        </div>
                    </Collapse>
                </div>

                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.modalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">{'Delete ' + this.props.nocServer.serverName + '?'}</div>
                </Modal>
            </div>
        );
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLAnchorElement>) {
        event.preventDefault();

        this.editServerForm.onSubmit();
        if (this.editServerForm.validate()) {
            const name = this.editServerForm.values.serverName;
            this.loading = true;
            const task = this.tasksStore.startSessionTask(`Update Server: ${this.props.nocServer.serverName}`);

            try {
                await this.nocServersStore.updateAsync(this.props.nocServer.serverName, this.editServerForm.values);
                task.complete();
            } catch (e: any) {
                if (e.modelState) {
                    this.editServerForm.clearGlobalErrors();
                    this.editServerForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.onToggle();
                this.loading = false;
            }
        } else {
            console.error('The Form is invalid');
        }
    }

    @bind
    @action
    private onToggle() {
        this.collapsed = !this.collapsed;
    }

    @computed
    private get editServerForm() {
        return new FormStore<Mri.NocServers.NocServer>(this.props.nocServer);
    }

    @observable
    private loading: boolean = false;
    @observable
    private collapsed = true;
    @observable
    private modalHandle = new ModalHandle();
    @inject
    private appStore!: AppStore;
    @inject
    private nocServersStore!: NocServersStore;
    @inject
    private tasksStore!: TasksStore;
}
